/**
 * État de l'application.
 *
 * Un module unique, sans bibliothèque de gestion d'état : React s'y abonne via
 * `useSyncExternalStore`. Pour une application à un écran, un gestionnaire
 * externe serait de la complexité qui ne sert ni le hors-ligne ni l'API.
 *
 * # Le contrat
 *
 * Toute action utilisateur suit exactement le même chemin :
 *
 *   1. construire une opération ;
 *   2. l'ajouter à la file **et** l'appliquer à la vue → l'écran est à jour
 *      immédiatement, avant tout accès réseau ;
 *   3. persister la file en IndexedDB ;
 *   4. signaler au moteur de synchronisation qu'il y a du travail.
 *
 * Aucune action n'attend le réseau. Aucune action ne modifie l'état serveur
 * local : celui-ci n'est écrit que par l'arrivée d'un delta.
 */

import type { Item, List, SyncDelta } from '@shared/types.ts';
import type { Op } from '@shared/ops.ts';
import { resolveDefaultList } from '@shared/ops.ts';
import {
  applyDelta,
  emptyState,
  project,
  visibleItems,
  visibleLists,
  type HouseholdState,
} from '@shared/merge.ts';
import { comparePosition, needsRenumber, positionAtEnd, positionBetween, renumber } from '@shared/position.ts';
import { planItemMove } from '../lib/reorder.ts';
import * as db from './db.ts';
import { deviceId, detectPlatform, localDeviceLabel, rememberDeviceLabel, suggestedLabel } from '../lib/device.ts';

export type SyncPhase = 'starting' | 'idle' | 'syncing' | 'offline' | 'degraded' | 'unauthorized';

export interface SyncStatus {
  phase: SyncPhase;
  /** Message affiché à l'utilisateur en cas de dégradation. */
  message: string | null;
  lastSyncAt: number | null;
}

export interface Snapshot {
  ready: boolean;
  householdId: string | null;
  /** État serveur + opérations en attente. C'est ce que l'écran montre. */
  view: HouseholdState;
  pendingCount: number;
  selectedListId: string | null;
  status: SyncStatus;
}

let serverState: HouseholdState = emptyState();
let queue: Op[] = [];
let householdId: string | null = null;
let selectedListId: string | null = null;
let ready = false;
let status: SyncStatus = { phase: 'starting', message: null, lastSyncAt: null };

let snapshot: Snapshot = compute();
const listeners = new Set<() => void>();
const localChangeListeners = new Set<() => void>();

// --- abonnement ------------------------------------------------------------

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): Snapshot {
  return snapshot;
}

/** Notifié quand l'utilisateur agit : le moteur de synchronisation s'en sert. */
export function subscribeToLocalChanges(listener: () => void): () => void {
  localChangeListeners.add(listener);
  return () => localChangeListeners.delete(listener);
}

function compute(): Snapshot {
  const view = householdId ? project(serverState, queue, householdId) : serverState;

  return {
    ready,
    householdId,
    view,
    pendingCount: queue.length,
    selectedListId: resolveSelection(view),
    status,
  };
}

function publish(): void {
  snapshot = compute();
  for (const listener of listeners) listener();
}

/**
 * La liste affichée : celle choisie par l'utilisateur si elle existe toujours,
 * sinon la liste par défaut du foyer.
 */
function resolveSelection(view: HouseholdState): string | null {
  if (selectedListId) {
    const chosen = view.lists.get(selectedListId);
    if (chosen && chosen.deleted_at === null) return selectedListId;
  }

  return resolveDefaultList([...view.lists.values()])?.id ?? null;
}

// --- amorçage --------------------------------------------------------------

export async function hydrate(id: string): Promise<void> {
  householdId = id;

  // L'état local est chargé AVANT toute requête : l'application est utilisable
  // instantanément, y compris sans réseau.
  [serverState, queue] = await Promise.all([db.loadServerState(), db.loadQueue()]);

  ready = true;
  publish();

  await ensureDeviceRegistered();
}

/** Inscrit l'appareil au premier lancement. La détection ne sert qu'ici. */
async function ensureDeviceRegistered(): Promise<void> {
  const id = deviceId();
  if (serverState.devices.has(id) || queue.some((op) => op.kind === 'device.register' && op.id === id)) return;

  const platform = detectPlatform();
  const label = localDeviceLabel() ?? suggestedLabel(platform);
  rememberDeviceLabel(label);

  await enqueue({ ...base(), kind: 'device.register', id, label, platform });
}

// --- intégration des deltas ------------------------------------------------

export async function ingestDelta(delta: SyncDelta): Promise<void> {
  await db.persistDelta(delta);
  serverState = applyDelta(serverState, delta);
  publish();
}

/** Retire de la file les opérations acquittées, et seulement celles-là. */
export async function acknowledge(appliedOpIds: readonly string[], rejectedOpIds: readonly string[]): Promise<void> {
  const gone = new Set([...appliedOpIds, ...rejectedOpIds]);
  if (gone.size === 0) return;

  await db.acknowledgeOps([...gone]);
  queue = queue.filter((op) => !gone.has(op.op_id));
  publish();
}

export function pendingOps(): readonly Op[] {
  return queue;
}

export function setStatus(next: SyncStatus): void {
  status = next;
  publish();
}

// --- actions ---------------------------------------------------------------

function base(): { op_id: string; client_ts: number } {
  return { op_id: crypto.randomUUID(), client_ts: Date.now() };
}

async function enqueue(op: Op): Promise<void> {
  // La file est persistée AVANT d'être publiée : si l'application est tuée
  // juste après l'affichage, l'opération est déjà sur disque.
  await db.enqueueOp(op);
  queue = [...queue, op];
  publish();

  for (const listener of localChangeListeners) listener();
}

export function selectList(id: string): void {
  selectedListId = id;
  publish();
}

export async function addItem(listId: string, label: string, quantity: string | null): Promise<void> {
  const trimmed = label.trim();
  if (trimmed.length === 0) return;

  const siblings = visibleItems(snapshot.view, listId);

  await enqueue({
    ...base(),
    kind: 'item.create',
    id: crypto.randomUUID(),
    list_id: listId,
    label: trimmed,
    quantity: quantity?.trim() || null,
    position: positionAtEnd(siblings),
    device_id: deviceId(),
  });
}

export async function toggleItem(item: Item): Promise<void> {
  await enqueue({
    ...base(),
    kind: 'item.check',
    id: item.id,
    // Valeur cible et non bascule : rejouer l'opération reste sans effet.
    is_checked: !item.is_checked,
    device_id: deviceId(),
  });
}

export async function renameItem(item: Item, label: string, quantity: string | null): Promise<void> {
  const trimmed = label.trim();
  if (trimmed.length === 0) return;

  await enqueue({ ...base(), kind: 'item.rename', id: item.id, label: trimmed, quantity: quantity?.trim() || null });
}

/**
 * Déplace un article au rang `targetIndex` de la liste **affichée**.
 *
 * Comme pour les listes, l'indexation fractionnaire fait qu'un déplacement
 * n'écrit **qu'une seule ligne** : aucune renumérotation, donc aucun lot
 * d'écritures à faire converger entre deux appareils hors ligne.
 *
 * Le rang est celui de l'affichage, cochés compris. Les voisins retenus pour
 * le calcul de position sont en revanche cherchés dans le **même groupe**
 * (coché / non coché) : `visibleItems` fait systématiquement basculer les
 * cochés en bas, une position calculée à cheval sur les deux groupes serait
 * donc annulée à l'affichage — l'article reviendrait à sa place sous les yeux
 * de l'utilisateur.
 */
export async function moveItem(item: Item, targetIndex: number): Promise<void> {
  const plan = planItemMove(snapshot.view, item, targetIndex);
  if (!plan) return; // ordre inchangé : rien à écrire.

  if (needsRenumber(plan.before?.position ?? null, plan.after?.position ?? null)) {
    const siblings = visibleItems(snapshot.view, item.list_id).filter((i) => i.id !== item.id);

    for (const { id, position } of renumber([...siblings].sort(comparePosition))) {
      await enqueue({ ...base(), kind: 'item.move', id, list_id: item.list_id, position });
    }

    // Les voisins sont relus par identifiant, et non recalculés : la
    // renumérotation a changé les positions, pas l'ordre.
    const before = plan.before ? (snapshot.view.items.get(plan.before.id) ?? null) : null;
    const after = plan.after ? (snapshot.view.items.get(plan.after.id) ?? null) : null;

    await enqueue({
      ...base(),
      kind: 'item.move',
      id: item.id,
      list_id: item.list_id,
      position: positionBetween(before?.position ?? null, after?.position ?? null),
    });
    return;
  }

  await enqueue({
    ...base(),
    kind: 'item.move',
    id: item.id,
    list_id: item.list_id,
    position: positionBetween(plan.before?.position ?? null, plan.after?.position ?? null),
  });
}

export async function deleteItem(item: Item): Promise<void> {
  await enqueue({ ...base(), kind: 'item.delete', id: item.id });
}

/** « Vider les articles cochés » : une opération par article, jamais un lot global. */
export async function clearChecked(listId: string): Promise<void> {
  const checked = visibleItems(snapshot.view, listId).filter((item) => item.is_checked);

  for (const item of checked) {
    await enqueue({ ...base(), kind: 'item.delete', id: item.id });
  }
}

export async function createList(name: string): Promise<string | null> {
  const trimmed = name.trim();
  if (trimmed.length === 0) return null;

  const id = crypto.randomUUID();
  const lists = visibleLists(snapshot.view);

  await enqueue({
    ...base(),
    kind: 'list.create',
    id,
    name: trimmed,
    position: positionAtEnd(lists),
    is_default: lists.length === 0,
  });

  selectList(id);
  return id;
}

export async function renameList(list: List, name: string): Promise<void> {
  const trimmed = name.trim();
  if (trimmed.length === 0) return;

  await enqueue({ ...base(), kind: 'list.rename', id: list.id, name: trimmed });
}

export async function deleteList(list: List): Promise<void> {
  await enqueue({ ...base(), kind: 'list.delete', id: list.id });
}

export async function setDefaultList(list: List): Promise<void> {
  // Une seule ligne touchée : le drapeau des autres listes n'est pas retiré,
  // l'exclusivité est tranchée à la lecture.
  await enqueue({ ...base(), kind: 'list.setDefault', id: list.id });
}

/**
 * Déplace une liste à un rang donné.
 *
 * Grâce à l'indexation fractionnaire, l'opération courante ne modifie **qu'une
 * ligne**. La renumérotation complète n'intervient que si la précision
 * flottante est épuisée, ce qui demande des dizaines d'insertions au même
 * endroit.
 */
export async function moveList(list: List, targetIndex: number): Promise<void> {
  const others = visibleLists(snapshot.view).filter((l) => l.id !== list.id);
  const index = Math.max(0, Math.min(targetIndex, others.length));

  const before = index > 0 ? (others[index - 1] as List) : null;
  const after = index < others.length ? (others[index] as List) : null;

  if (needsRenumber(before?.position ?? null, after?.position ?? null)) {
    for (const { id, position } of renumber([...others].sort(comparePosition))) {
      await enqueue({ ...base(), kind: 'list.move', id, position });
    }

    const refreshed = visibleLists(snapshot.view).filter((l) => l.id !== list.id);
    const newBefore = index > 0 ? (refreshed[index - 1] as List) : null;
    const newAfter = index < refreshed.length ? (refreshed[index] as List) : null;

    await enqueue({
      ...base(),
      kind: 'list.move',
      id: list.id,
      position: positionBetween(newBefore?.position ?? null, newAfter?.position ?? null),
    });
    return;
  }

  await enqueue({
    ...base(),
    kind: 'list.move',
    id: list.id,
    position: positionBetween(before?.position ?? null, after?.position ?? null),
  });
}

export async function renameDevice(id: string, label: string): Promise<void> {
  const trimmed = label.trim();
  if (trimmed.length === 0) return;

  if (id === deviceId()) rememberDeviceLabel(trimmed);
  await enqueue({ ...base(), kind: 'device.rename', id, label: trimmed });
}

/** Change de foyer : tout l'état local est effacé, file comprise. */
export async function reset(): Promise<void> {
  await db.clearAll();
  serverState = emptyState();
  queue = [];
  selectedListId = null;
  publish();
}
